import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  LEGAL_FORM_LABELS, MANAGEMENT_MODE_LABELS, STATUS_LABELS,
} from "@/constants/group";

const TEXT_FIELDS = [
  { key: "denomination", label: "Dénomination", required: true, span: 2 },
  { key: "trade_name", label: "Nom commercial", span: 2 },
  { key: "siren", label: "SIREN" },
  { key: "siret", label: "SIRET" },
  { key: "vat_number", label: "N° TVA intracom." },
  { key: "share_capital", label: "Capital social (€)", type: "number" },
  { key: "creation_date", label: "Date de création", type: "date" },
  { key: "fiscal_year_end", label: "Clôture d'exercice (MM-JJ)", placeholder: "12-31" },
  { key: "activity", label: "Activité", span: 2 },
  { key: "corporate_purpose", label: "Objet social", span: 2, textarea: true },
  { key: "legal_representative", label: "Représentant légal", span: 2 },
  { key: "contact_email", label: "E-mail de contact", type: "email" },
  { key: "contact_phone", label: "Téléphone" },
  { key: "main_bank", label: "Banque principale", span: 2 },
  { key: "accountant", label: "Cabinet comptable", span: 2 },
  { key: "address", label: "Adresse du siège", span: 2 },
  { key: "postal_code", label: "Code postal" },
  { key: "city", label: "Ville" },
  { key: "internal_notes", label: "Notes internes", span: 2, textarea: true },
];

const REFERENCE_FIELDS = [
  { key: "activity", label: "Activité (référence)" },
  { key: "ownership_pct", label: "Pourcentage détenu (%)", type: "number" },
  { key: "estimated_value", label: "Valorisation estimée (€)", type: "number" },
  { key: "revenue", label: "Chiffre d'affaires (€)", type: "number" },
  { key: "result", label: "Résultat (€)", type: "number" },
  { key: "cash", label: "Trésorerie (€)", type: "number" },
  { key: "dividends_paid", label: "Dividendes versés (€)", type: "number" },
  { key: "notes", label: "Notes complémentaires", textarea: true, span: 2 },
];

export default function CompanyIdentityTab({ company, onSaved }) {
  const { activeRole } = useAuth();
  const canEdit = ["owner", "admin", "manager"].includes(activeRole);
  const [form, setForm] = useState({ ...company });
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ ...company }); }, [company]);
  useEffect(() => {
    api.get("/companies")
       .then(({ data }) => setCompanies(data.items || []))
       .catch((err) => console.error("Load companies failed:", err));
  }, []);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const updRef = (k) => (v) => setForm((f) => ({ ...f, reference_data: { ...(f.reference_data || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    const payload = { ...form };
    delete payload.id; delete payload.organization_id;
    delete payload.created_at; delete payload.updated_at;
    delete payload.name; delete payload.is_demo;
    // Coerce numbers
    ["share_capital"].forEach((k) => { if (payload[k] === "") payload[k] = null; else if (payload[k] != null) payload[k] = parseFloat(payload[k]); });
    if (payload.reference_data) {
      ["ownership_pct", "estimated_value", "revenue", "result", "cash", "dividends_paid"].forEach((k) => {
        if (payload.reference_data[k] === "") payload.reference_data[k] = null;
        else if (payload.reference_data[k] != null) payload.reference_data[k] = parseFloat(payload.reference_data[k]);
      });
    }
    try {
      await api.patch(`/companies/${company.id}`, payload);
      toast.success("Fiche mise à jour");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    }
    setSaving(false);
  };

  const isReference = form.management_mode === "reference";

  return (
    <div className="space-y-6">
      {/* Config bar */}
      <div className="card-elevated p-6">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <Label>Forme juridique</Label>
            <Select value={form.legal_form} onValueChange={upd("legal_form")} disabled={!canEdit}>
              <SelectTrigger data-testid="identity-legal-form"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(LEGAL_FORM_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Régime fiscal</Label>
            <Select value={form.tax_regime || ""} onValueChange={upd("tax_regime")} disabled={!canEdit}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {["IS", "IR", "MICRO", "EXO", "N/A"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Mode de gestion</Label>
            <Select value={form.management_mode || "full"} onValueChange={upd("management_mode")} disabled={!canEdit}>
              <SelectTrigger data-testid="identity-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MANAGEMENT_MODE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Statut</Label>
            <Select value={form.status || "active"} onValueChange={upd("status")} disabled={!canEdit}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-3 items-center">
          <div className="flex items-center gap-3">
            <Switch checked={!!form.vat_applicable} onCheckedChange={upd("vat_applicable")} disabled={!canEdit} data-testid="identity-vat-switch" />
            <Label className="!mt-0">Assujetti à la TVA</Label>
          </div>
          <div>
            <Label>Périodicité TVA</Label>
            <Select value={form.vat_periodicity || ""} onValueChange={upd("vat_periodicity")} disabled={!canEdit || !form.vat_applicable}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {["mensuelle", "trimestrielle", "annuelle", "franchise", "n/a"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Société mère</Label>
            <Select
              value={form.parent_company_id || "none"}
              onValueChange={(v) => upd("parent_company_id")(v === "none" ? null : v)}
              disabled={!canEdit}>
              <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {companies.filter((c) => c.id !== company.id).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Fields */}
      {!isReference && (
        <div className="card-elevated p-6">
          <div className="text-overline mb-4">Identité complète</div>
          <div className="grid gap-4 md:grid-cols-2">
            {TEXT_FIELDS.map((f) => (
              <Field key={f.key} field={f} value={form[f.key] || ""} onChange={upd(f.key)} disabled={!canEdit} />
            ))}
          </div>
        </div>
      )}

      {isReference && (
        <div className="card-elevated p-6">
          <div className="text-overline mb-4">Données de référence</div>
          <p className="text-sm text-muted-foreground mb-4">
            En mode « Référence », seuls les indicateurs clés sont demandés. La gestion opérationnelle reste externe.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <Field field={{ key: "denomination", label: "Dénomination", span: 2, required: true }}
                   value={form.denomination || ""} onChange={upd("denomination")} disabled={!canEdit} />
            <Field field={{ key: "siren", label: "SIREN" }} value={form.siren || ""} onChange={upd("siren")} disabled={!canEdit} />
            {REFERENCE_FIELDS.map((f) => (
              <Field key={f.key} field={f}
                     value={(form.reference_data || {})[f.key] || ""}
                     onChange={updRef(f.key)} disabled={!canEdit} />
            ))}
          </div>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="btn-pill" data-testid="identity-save">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer la fiche"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ field, value, onChange, disabled }) {
  const span = field.span === 2 ? "md:col-span-2" : "";
  const inputProps = {
    type: field.type || "text",
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    disabled,
    placeholder: field.placeholder,
    "data-testid": `identity-${field.key}`,
  };
  return (
    <div className={span}>
      <Label>{field.label}{field.required ? " *" : ""}</Label>
      {field.textarea
        ? <Textarea rows={2} {...inputProps} className="mt-1.5" />
        : <Input {...inputProps} className="mt-1.5" />}
    </div>
  );
}
